import classnames from 'classnames';
import PropTypes from 'prop-types';
import React, { useRef } from 'react';

import MediaCarouselSplide, {
  DEFAULT_SPLIDE_OPTIONS
} from 'dpl/common/components/MediaCarouselSplide';
import withLazyLoading from 'dpl/decorators/withLazyLoading';
import { CAROUSEL_TYPES } from 'dpl/puppy_search_results/utils/constants';
import { PhotographPropTypes } from 'dpl/shared/utils/customPropTypes';

import CardMediaCarouselFile from './CardMediaCarouselFile';

const COMMON_ARROW_CLASS_NAME = 'bg-white black f6';

const CardMediaCarousel = React.memo(function CardMediaCarousel({
  mediaFiles,
  smartImageProps,
  muxVideoProps,
  isInViewport,
  setLazyRef,
  showCaptions,
  isPuppyCard,
  analytics,
  splideOptions: splideOptionsOverrides
}) {
  const splideRef = useRef(null);

  const splideOptions = {
    ...DEFAULT_SPLIDE_OPTIONS,
    gap: 4,
    type: CAROUSEL_TYPES.SLIDE,
    fixedWidth: 260,
    autoWidth: true,
    // Inset matching the breeder card's own gutter. Callers whose strip starts
    // flush with its container (the similar breeds breeder list) override it.
    padding: 20,
    ...splideOptionsOverrides
  };

  return (
    <div className="CardMediaCarousel">
      <div
        style={{
          minHeight: smartImageProps.height || '1px',
          minWidth: smartImageProps.width || '1px'
        }}
        ref={setLazyRef}
      >
        {isInViewport && (
          <MediaCarouselSplide
            splideClassName="BreederSearchResultsPhoto h-100 f0"
            leftArrowClassName={classnames(COMMON_ARROW_CLASS_NAME, {
              ml8: !isPuppyCard,
              ml4: isPuppyCard
            })}
            rightArrowClassName={classnames(COMMON_ARROW_CLASS_NAME, 'mr4')}
            mediaFiles={mediaFiles}
            ref={splideRef}
            CarouselMediaComponent={CardMediaCarouselFile}
            smartImageProps={smartImageProps}
            muxVideoProps={muxVideoProps}
            splideOptions={splideOptions}
            showCaptions={showCaptions}
            hasPhotoBullets={isPuppyCard}
            bulletStyle={{ transform: 'scale(0.85)', margin: '2px' }}
            analytics={analytics}
            roundedCorners
          />
        )}
      </div>
    </div>
  );
});

CardMediaCarousel.propTypes = {
  mediaFiles: PropTypes.arrayOf(
    PropTypes.shape({
      ...PhotographPropTypes,
      url: PropTypes.string,
      urls: PropTypes.arrayOf(PropTypes.string)
    })
  ),
  // eslint-disable-next-line react/forbid-prop-types
  smartImageProps: PropTypes.object.isRequired,
  // eslint-disable-next-line react/forbid-prop-types
  muxVideoProps: PropTypes.object,
  showCaptions: PropTypes.bool,
  isInViewport: PropTypes.bool.isRequired,
  setLazyRef: PropTypes.func,
  isPuppyCard: PropTypes.bool,
  analytics: PropTypes.shape({
    position: PropTypes.number,
    view: PropTypes.string
  }),
  // merged over the defaults above
  // eslint-disable-next-line react/forbid-prop-types
  splideOptions: PropTypes.object
};

CardMediaCarousel.defaultProps = {
  setLazyRef: null,
  mediaFiles: [],
  showCaptions: false,
  isPuppyCard: false,
  analytics: {},
  muxVideoProps: null,
  splideOptions: null
};

export default withLazyLoading({
  defaultLazy: true
})(CardMediaCarousel);
